// Native Studio — the shared "Go live" form: broadcast title + optional show
// picker → room-create → into /studio/$slug. createRoom returns no slug, so we
// poll mineActive after the create and navigate once the newly-created room
// surfaces (matched by its livekit_room_name, so a pre-existing active room can't
// race us). Used by the Studio dashboard and the admin go-live entry.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { liveRoomService } from './liveRoomService';
import { roomQueries } from './roomQueries';
import { listShows } from './showActions';

// Free-platform simulcast opt-in (per broadcast). Each platform's RTMP URL is a
// server secret; these toggles just choose which to push to.
const SIMULCAST_OPTIONS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'rumble', label: 'Rumble' },
  { id: 'twitch', label: 'Twitch' },
  { id: 'kick', label: 'Kick' },
] as const;

export function GoLiveForm({ hostId }: { hostId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [showId, setShowId] = useState('');
  const [simulcast, setSimulcast] = useState<string[]>([]);
  // The livekit_room_name we're waiting to resolve a slug for (set after create).
  const [targetRoom, setTargetRoom] = useState<string | null>(null);

  const shows = useQuery({
    queryKey: ['shows', hostId],
    enabled: Boolean(hostId),
    queryFn: () => listShows(hostId),
  });
  const mine = useQuery({ ...roomQueries.mineActive(hostId), enabled: Boolean(targetRoom) });
  const create = useMutation({
    mutationFn: (vars: { name: string; showId?: string; simulcastTargets?: string[] }) =>
      liveRoomService.createRoom(vars),
    onSuccess: (handle) => {
      setTargetRoom(handle.roomId); // roomId === livekit_room_name
      void qc.invalidateQueries({ queryKey: ['live-room-mine', hostId] });
    },
  });

  useEffect(() => {
    if (targetRoom && mine.data?.livekitRoomName === targetRoom && mine.data.slug) {
      void navigate({ to: '/studio/$slug', params: { slug: mine.data.slug }, replace: true });
    }
  }, [targetRoom, mine.data, navigate]);

  const trimmed = title.trim();
  const busy = create.isPending || Boolean(targetRoom);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (trimmed)
          create.mutate({
            name: trimmed,
            showId: showId || undefined,
            simulcastTargets: simulcast.length > 0 ? simulcast : undefined,
          });
      }}
      className="max-w-md space-y-4"
    >
      <div>
        <label
          htmlFor="broadcast-title"
          className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint"
        >
          Broadcast title
        </label>
        <input
          id="broadcast-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
          placeholder="Tuesday Panel"
          className="mt-1 w-full rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
        />
      </div>
      {(shows.data ?? []).length > 0 && (
        <div>
          <label
            htmlFor="broadcast-show"
            className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint"
          >
            Show (optional)
          </label>
          <select
            id="broadcast-show"
            value={showId}
            onChange={(e) => setShowId(e.target.value)}
            className="mt-1 w-full rounded-md border border-video-line bg-video-inset px-3 py-2 font-sans text-sm text-video-text outline-none focus:border-video-line-strong"
          >
            <option value="">No show (ad-hoc)</option>
            {shows.data!.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      )}
      <fieldset>
        <legend className="font-sans text-xs font-medium uppercase tracking-[0.12em] text-video-text-faint">
          Also stream to (optional)
        </legend>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2">
          {SIMULCAST_OPTIONS.map((p) => (
            <label
              key={p.id}
              className="flex items-center gap-2 font-sans text-sm text-video-text-muted"
            >
              <input
                type="checkbox"
                checked={simulcast.includes(p.id)}
                onChange={(e) =>
                  setSimulcast((prev) =>
                    e.target.checked ? [...prev, p.id] : prev.filter((x) => x !== p.id),
                  )
                }
                className="accent-oxblood-live"
              />
              {p.label}
            </label>
          ))}
        </div>
      </fieldset>
      <button
        type="submit"
        disabled={!trimmed || busy}
        className="rounded-md bg-oxblood-live px-5 py-2 font-sans text-sm font-semibold text-white transition hover:opacity-90 disabled:opacity-50"
      >
        {busy ? 'Starting…' : 'Go live'}
      </button>
      {create.isError && (
        <p className="font-sans text-xs text-oxblood-live">
          {create.error instanceof Error ? create.error.message : 'Could not start the broadcast'}
        </p>
      )}
    </form>
  );
}
